console.log('[table-paginator] script loaded');

class TablePaginator extends HTMLElement {
	static _cssAdded = false;

	connectedCallback() {
		console.log('[table-paginator] connectedCallback fired');
		this._tbody = this.querySelector('tbody');
		if (!this._tbody) { console.warn('[table-paginator] no tbody found'); return; }

		this._allRows = Array.from(this._tbody.querySelectorAll(':scope > tr'));
		if (this._allRows.length === 0) { console.warn('[table-paginator] no rows found'); return; }
		console.log('[table-paginator] rows found:', this._allRows.length);

		this._searchInput = this.querySelector('[data-paginator-search]');
		this._perPageSelect = this.querySelector('[data-paginator-per-page]');
		this._infoEl = this.querySelector('[data-paginator-info]');
		this._navEl = this.querySelector('[data-paginator-nav]');

		this._currentPage = 1;
		this._perPage = this._perPageSelect
			? parseInt(this._perPageSelect.value, 10)
			: parseInt(this.getAttribute('per-page') || '25', 10);
		this._searchTerm = '';
		this._renderPending = false;

		this._injectCss();

		if (this._searchInput) {
			this._searchInput.addEventListener('input', () => {
				this._searchTerm = this._searchInput.value.trim();
				this._currentPage = 1;
				this._scheduleRender();
			});
		}

		if (this._perPageSelect) {
			this._perPageSelect.addEventListener('change', () => {
				this._perPage = parseInt(this._perPageSelect.value, 10);
				this._currentPage = 1;
				this._scheduleRender();
			});
		}

		this._observer = new MutationObserver(() => {
			if (!this._renderPending) {
				this._currentPage = 1;
			}
			this._scheduleRender();
		});
		this._startObserving();

		this._render();
	}

	_injectCss() {
		if (TablePaginator._cssAdded || !('replaceSync' in CSSStyleSheet.prototype)) return;
		TablePaginator._cssAdded = true;

		const sheet = new CSSStyleSheet();
		sheet.replaceSync(`
			.paginator--hide { display: none !important; }

			table-paginator .table-paginator-toolbar {
				display: flex;
				flex-wrap: wrap;
				justify-content: space-between;
				align-items: flex-end;
				gap: 0.75rem;
				margin-block-end: 1rem;
			}

			table-paginator .table-paginator-search {
				display: flex;
				flex-direction: column;
				gap: 0.25rem;
			}

			table-paginator .table-paginator-per-page {
				display: inline-flex;
				align-items: center;
				gap: 0.5rem;
			}

			table-paginator .table-paginator-footer {
				display: flex;
				flex-wrap: wrap;
				justify-content: space-between;
				align-items: center;
				gap: 0.75rem;
				margin-block-start: 0.75rem;
			}

			table-paginator .table-paginator-info {
				margin: 0;
				font-size: var(--font-size-sm, 0.875rem);
			}

			table-paginator .table-paginator-nav {
				display: flex;
				flex-wrap: wrap;
				gap: 0.25rem;
				align-items: center;
			}

			table-paginator .paginator-btn {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				min-width: 2rem;
				min-height: 2rem;
				padding: 0.25rem 0.5rem;
				border: 1px solid var(--input-border-color, #b0b1b2);
				background: transparent;
				color: inherit;
				cursor: pointer;
				font-size: var(--font-size-sm, 0.875rem);
				font-family: inherit;
				border-radius: 0.25rem;
				line-height: 1.5;
			}

			table-paginator .paginator-btn:is(:hover, :focus-visible):not(:disabled) {
				background-color: var(--color-primary-100, #f2f4f9);
				border-color: var(--color-primary-base, #23418f);
				color: var(--color-primary-base, #23418f);
				outline: none;
			}

			table-paginator .paginator-btn[aria-current="page"] {
				background-color: var(--color-primary-base, #23418f);
				border-color: var(--color-primary-base, #23418f);
				color: var(--color-white, #ffffff);
			}

			table-paginator .paginator-btn:disabled {
				opacity: 0.4;
				cursor: not-allowed;
			}

			table-paginator .paginator-ellipsis {
				padding: 0.25rem;
				font-size: var(--font-size-sm, 0.875rem);
			}
		`);
		document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet];
	}

	_startObserving() {
		this._observer.observe(this._tbody, {
			attributes: true,
			attributeFilter: ['class', 'hidden'],
			subtree: true,
		});
	}

	_scheduleRender() {
		if (this._renderPending) return;
		this._renderPending = true;
		requestAnimationFrame(() => {
			this._renderPending = false;
			this._render();
		});
	}

	_isFilterHidden(row) {
		if (row.hasAttribute('hidden')) return true;
		for (const cls of row.classList) {
			if (cls.startsWith('filter-') && cls.endsWith('--hide')) return true;
		}
		return false;
	}

	_matchesSearch(row) {
		if (!this._searchTerm) return true;
		return row.textContent.toLowerCase().includes(this._searchTerm.toLowerCase());
	}

	_render() {
		this._observer.disconnect();

		const searchPool = this._allRows.filter((row) => this._matchesSearch(row));
		const visiblePool = searchPool.filter((row) => !this._isFilterHidden(row));

		const total = visiblePool.length;
		const totalPages = Math.max(1, Math.ceil(total / this._perPage));
		if (this._currentPage > totalPages) this._currentPage = totalPages;

		const start = (this._currentPage - 1) * this._perPage;
		const end = Math.min(start + this._perPage, total);
		const pageSet = new Set(visiblePool.slice(start, end));

		for (const row of this._allRows) {
			if (pageSet.has(row)) {
				row.classList.remove('paginator--hide');
			} else {
				row.classList.add('paginator--hide');
			}
		}

		if (this._infoEl) {
			this._infoEl.textContent =
				total === 0
					? 'No entries found'
					: `Showing ${start + 1} to ${end} of ${total} entries`;
		}

		if (this._navEl) {
			this._buildNav(totalPages);
		}

		this._startObserving();
	}

	_buildNav(totalPages) {
		this._navEl.innerHTML = '';
		if (totalPages <= 1) return;

		const cur = this._currentPage;

		const mkBtn = (label, page, disabled, current) => {
			const btn = document.createElement('button');
			btn.type = 'button';
			btn.textContent = label;
			btn.className = 'paginator-btn';
			if (current) btn.setAttribute('aria-current', 'page');
			if (disabled) {
				btn.disabled = true;
			} else {
				btn.addEventListener('click', () => {
					this._currentPage = page;
					this._render();
				});
			}
			return btn;
		};

		const prevBtn = mkBtn('Previous', cur - 1, cur === 1);
		prevBtn.classList.add('is-prev');
		this._navEl.appendChild(prevBtn);

		for (const p of this._pageRange(cur, totalPages)) {
			if (p === '…') {
				const span = document.createElement('span');
				span.textContent = '…';
				span.className = 'paginator-ellipsis';
				span.setAttribute('aria-hidden', 'true');
				this._navEl.appendChild(span);
			} else {
				this._navEl.appendChild(mkBtn(p, p, false, p === cur));
			}
		}

		const nextBtn = mkBtn('Next', cur + 1, cur === totalPages);
		nextBtn.classList.add('is-next');
		this._navEl.appendChild(nextBtn);
	}

	_pageRange(current, total) {
		if (total <= 7) {
			return Array.from({ length: total }, (_, i) => i + 1);
		}
		if (current <= 4) {
			return [1, 2, 3, 4, 5, '…', total];
		}
		if (current >= total - 3) {
			return [1, '…', total - 4, total - 3, total - 2, total - 1, total];
		}
		return [1, '…', current - 1, current, current + 1, '…', total];
	}
}

if ('customElements' in window) {
	customElements.define('table-paginator', TablePaginator);
}
